import { Popover, PopoverContent, PopoverTrigger } from '@crowdfpl/ui-web/popover';
import { Separator } from '@crowdfpl/ui-web/separator';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@crowdfpl/ui-web/sheet';
import { Info, X } from 'lucide-react';
import { useState } from 'react';
import { cn } from '@/helpers/utils';
import { useDebugMode } from '@/hooks/utils/use-debug-mode';
import { useIsMobile } from '@/hooks/utils/use-mobile';

export interface RatingFactorsDebug {
  N: number;
  Nmin: number;
  Nmax: number;
  kPop_formula: string;
  hours_before_deadline?: number;
  kEarly_bracket?: string;
}

export interface RatingFactorsData {
  duelWeight: string;
  duelWeightDetail: string;
  earlyBonusValue: string;
  earlyBonusDetail: string;
  isDuelOfTheWeek?: boolean;
  ratingFactorsDebug?: RatingFactorsDebug;
  /** Human vote counts per player (CrowdFPL excludes bots). When both set, low-votes note uses per-side threshold. */
  humanVotesPlayerA?: number;
  humanVotesPlayerB?: number;
}

interface RatingFactorsChipProps {
  data: RatingFactorsData;
  className?: string;
}

/** Форматирует duel_weight с двумя знаками после запятой */
function formatDuelWeight(weight: string): string {
  const n = parseFloat(weight);
  if (Number.isNaN(n)) return weight;
  return n.toFixed(2);
}

/** Извлекает total votes из duel_weight_detail (формат "X/Y votes") */
function getTotalVotesFromDetail(detail: string): number | null {
  const match = detail.match(/^(\d+)\//);
  return match ? parseInt(match[1], 10) : null;
}

/** Нормализует duel_weight_detail: при totalVotes > Nmax (из-за кэша) показывает max(Nmax, totalVotes) в знаменателе */
function normalizeDuelWeightDetail(detail: string): string {
  const match = detail.match(/^(\d+)\/(\d+|—)\s+votes$/);
  if (!match) return detail;
  const totalVotes = parseInt(match[1], 10);
  const denom = match[2];
  if (denom === '—') return detail;
  const nmax = parseInt(denom, 10);
  const displayDenom = Math.max(nmax, totalVotes);
  return `${totalVotes}/${displayDenom} votes`;
}

function RatingImpactBody({ data, showDebug }: { data: RatingFactorsData; showDebug: boolean }) {
  const totalVotes = getTotalVotesFromDetail(data.duelWeightDetail);
  const humanA = data.humanVotesPlayerA;
  const humanB = data.humanVotesPlayerB;
  const hasHumanPerSide = humanA != null && humanB != null;
  const showPerSideThresholdNote = hasHumanPerSide && !(humanA > 0 && humanB > 0);
  /** When per-side counts are not on the client, only total N from API is available — use generic copy (not the per-player rule). */
  const showLowParticipationNote =
    !hasHumanPerSide && totalVotes != null && totalVotes < 10;

  return (
    <div className="space-y-2">
      {data.isDuelOfTheWeek && (
        <p className="text-xs text-foreground">
          <span className="text-muted-foreground">Duel of the Week = </span>
          <span className="font-semibold">double points!</span>
        </p>
      )}

      {showPerSideThresholdNote && (
        <p className="text-[10px] text-muted-foreground/70 italic leading-snug">
          Rating is calculated when at least one manager has voted for each player.
        </p>
      )}
      {showLowParticipationNote && (
        <p className="text-[10px] text-muted-foreground/70 italic leading-snug">
          Rating impact scales with manager participation; very low turnout may limit changes.
        </p>
      )}

      <p className="text-xs text-foreground">
        <span className="text-muted-foreground">Duel Weight = </span>
        <span className="font-semibold">{formatDuelWeight(data.duelWeight)}</span>
        <span className="text-muted-foreground"> ({normalizeDuelWeightDetail(data.duelWeightDetail)})</span>
      </p>

      <p className="text-xs text-foreground">
        <span className="text-muted-foreground">Early Bonus = </span>
        <span className="font-semibold">{data.earlyBonusValue}</span>
        <span className="text-muted-foreground"> ({data.earlyBonusDetail})</span>
      </p>

      {showDebug && data.ratingFactorsDebug && (
        <>
          <Separator className="bg-border" />
          <div className="space-y-1.5 rounded bg-muted/50 p-2 text-[10px] font-mono">
            <p className="font-semibold text-foreground">K-pop formula:</p>
            <p className="text-muted-foreground break-all">{data.ratingFactorsDebug.kPop_formula}</p>
            {data.ratingFactorsDebug.hours_before_deadline != null && (
              <p className="text-muted-foreground">
                hours_before_deadline: {data.ratingFactorsDebug.hours_before_deadline.toFixed(1)}
              </p>
            )}
            {data.ratingFactorsDebug.kEarly_bracket && (
              <p className="text-muted-foreground">K-early bracket: {data.ratingFactorsDebug.kEarly_bracket}</p>
            )}
          </div>
        </>
      )}

      <Separator className="bg-border" />

      <div>
        <button
          type="button"
          onClick={() => window.open('/rating', '_blank', 'noopener,noreferrer')}
          className="text-xs font-medium text-muted-foreground hover:text-foreground transition-colors inline-flex items-center gap-1 cursor-pointer"
        >
          How rating works →
        </button>
      </div>
    </div>
  );
}

export function RatingFactorsChip({ data, className }: RatingFactorsChipProps) {
  const isMobile = useIsMobile();
  const showDebug = useDebugMode();
  const [open, setOpen] = useState(false);

  const stopAndOpen = (e: React.MouseEvent | React.PointerEvent | React.TouchEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setOpen((prev) => !prev);
  };

  const stopOnly = (e: React.PointerEvent | React.TouchEvent) => {
    e.stopPropagation();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      e.stopPropagation();
      setOpen((prev) => !prev);
    }
  };

  const chipVisual = (
    <span
      className={cn(
        'inline-flex items-center rounded-full font-medium',
        'text-[10px] gap-1.5 px-1.5 py-0.5',
        'border border-border bg-muted/50 text-muted-foreground',
        'hover:bg-muted hover:border-muted-foreground/30 transition-colors',
        'cursor-pointer select-none leading-tight',
        className,
      )}
    >
      <Info className="h-3 w-3 shrink-0" />
      <span className="hidden sm:inline">Rating impact</span>
      <span className="sm:hidden">Impact</span>
    </span>
  );

  const chipElement = (
    <span
      role="button"
      tabIndex={0}
      data-rating-impact-chip
      onClickCapture={stopAndOpen}
      onPointerDown={stopOnly}
      onPointerUp={stopOnly}
      onTouchStart={stopOnly}
      onTouchEnd={stopOnly}
      onKeyDown={handleKeyDown}
      className="inline-flex self-start -m-1.5 p-1.5 !min-h-0 !min-w-0"
      aria-label="Rating impact for this duel"
    >
      {chipVisual}
    </span>
  );

  if (isMobile) {
    return (
      <>
        {chipElement}
        <Sheet open={open} onOpenChange={setOpen}>
          <SheetContent side="bottom" className="rounded-t-xl max-h-[70vh] pb-safe">
            <SheetHeader className="mb-3">
              <SheetTitle className="text-sm font-semibold text-foreground">
                Rating impact
              </SheetTitle>
            </SheetHeader>
            <RatingImpactBody data={data} showDebug={showDebug} />
          </SheetContent>
        </Sheet>
      </>
    );
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        {chipElement}
      </PopoverTrigger>
      <PopoverContent
        className="w-64 p-3"
        side="bottom"
        align="start"
        portal
      >
        <div
          onClick={(e) => e.stopPropagation()}
          onMouseDown={(e) => e.stopPropagation()}
        >
          <div className="flex items-center justify-between mb-2">
            <p className="text-xs font-semibold text-foreground">Rating impact</p>
            <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setOpen(false);
              }}
              onMouseDown={(e) => {
                e.preventDefault();
                e.stopPropagation();
              }}
              className="rounded-sm opacity-70 hover:opacity-100 transition-opacity"
              aria-label="Close"
            >
              <X className="h-3.5 w-3.5 text-muted-foreground" />
            </button>
          </div>
          <RatingImpactBody data={data} showDebug={showDebug} />
        </div>
      </PopoverContent>
    </Popover>
  );
}
